import classNames from 'classnames';
import dayjs from 'dayjs';
import FocusTrap from 'focus-trap-react';
import { getIn, useFormikContext } from 'formik';
import React, { useRef, useState, useId, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { usePopper } from 'react-popper';

import { useLocalization } from 'redux/localization/utils/hooks';

import FormError from './common/FormError';

// eslint-disable-next-line import/no-cycle
import { normalizeDate } from '.';

const DatePicker = (props) => {
  const {
    onChange,
    required,
    label,
    labelSROnly = false,
    disabled,
    field,
    displayErrors = true,
    form,
    describedByLabel,
    describedByContent,
    disabledDays
  } = props;

  const { t } = useLocalization();

  const [isPopperOpen, setIsPopperOpen] = useState(false);

  const popperRef = useRef(null);
  const buttonRef = useRef(null);
  const [popperElement, setPopperElement] = useState(null);

  const popper = usePopper(popperRef.current, popperElement, {
    placement: 'bottom-start'
  });

  const { name, onChange: onFieldChange, value } = field;

  const formikContext = useFormikContext();
  const fieldId = useId();
  const { setFieldValue } = formikContext;

  useEffect(() => {
    if (value) {
      setFieldValue(name, normalizeDate(value), false);
    }
  }, []); // eslint-disable-line

  const { id = `c7-${fieldId}-${name}` } = props;
  const { touched, errors } = form;

  const error = getIn(errors, name);
  const touch = getIn(touched, name);

  const closePopper = () => {
    setIsPopperOpen(false);
    buttonRef?.current?.focus();
  };

  const handleInputChange = (e) => {
    onFieldChange(e);
    if (onChange) {
      setTimeout(() => onChange(e), 1);
    }
  };

  const handleButtonClick = () => {
    setIsPopperOpen(true);
  };

  const handleDaySelect = (date) => {
    const newEvent = {
      target: { value: date, id: name, name }
    };

    if (date) {
      newEvent.target.value = dayjs(date).format('MMM D, YYYY');
    }

    onFieldChange(newEvent);
    closePopper();
    if (onChange) {
      setTimeout(() => onChange(newEvent), 1);
    }
  };

  const labelId = `${id}-label`;
  const errorId = `${id}-error`;

  let ariaDescribedBy;
  if (describedByLabel) {
    ariaDescribedBy = describedByLabel;
  }
  if (touch && error) {
    ariaDescribedBy = errorId;
  }

  let currentValue;
  if (value && dayjs(value, 'MMM-DD-YYYY', true).isValid()) {
    currentValue = dayjs(value).toDate();
  }

  return (
    <div className="c7-form__field" ref={popperRef}>
      <label
        htmlFor={id}
        id={labelId}
        className={classNames(
          required && 'c7-required',
          labelSROnly && 'c7-sr-only'
        )}
      >
        {label}
      </label>
      <div className="c7-date-picker-input">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          className=""
          autoComplete="off"
          aria-required={required}
          name={name}
          id={id}
          aria-describedby={ariaDescribedBy}
          aria-labelledby={labelId}
          aria-invalid={!!error && touch}
          disabled={disabled}
          placeholder="MMM DD YYYY"
        />
        <button
          ref={buttonRef}
          type="button"
          className="c7-date-picker-toggle"
          aria-label={t('general.accessibility.pickDate')}
          onClick={handleButtonClick}
          disabled={disabled}
          aria-disabled={disabled}
        >
          <span
            role="img"
            aria-label={t('general.accessibility.iconCalendarDescription')}
          >
            📅
          </span>
        </button>
      </div>
      {describedByContent}
      {displayErrors && (
        <FormError touch={touch} error={error} id={errorId} label={label} />
      )}
      {isPopperOpen && (
        <FocusTrap
          active
          focusTrapOptions={{
            initialFocus: false,
            allowOutsideClick: true,
            clickOutsideDeactivates: true,
            onDeactivate: closePopper
          }}
        >
          <div
            tabIndex={-1}
            style={popper.styles.popper}
            className="c7-date-picker-dropdown"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...popper.attributes.popper}
            ref={setPopperElement}
            role="dialog"
          >
            <DayPicker
              initialFocus={isPopperOpen}
              mode="single"
              defaultMonth={currentValue}
              selected={currentValue}
              disabled={disabledDays}
              onSelect={handleDaySelect}
            />
          </div>
        </FocusTrap>
      )}
    </div>
  );
};

export default DatePicker;
